import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, EyeOff, CheckCircle, ArrowLeft, Loader2 } from "lucide-react";
import logo from "../../images/Nkenne Ai-01 2.png";
import loginIllustration from "../../images/Learning language with friend.png";
import { verifyResetToken, resetPassword } from "../../services/authService";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);

  const token = searchParams.get("token");

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        toast.error("Invalid reset link. No token provided.", {
          position: "top-right",
          autoClose: 4000,
        });
        setIsVerifyingToken(false);
        setTimeout(() => navigate("/forgot-password"), 2000);
        return;
      }

      try {
        console.log("Verifying reset token:", token);
        const response = await verifyResetToken(token);
        console.log("Token verification response:", response);
        
        if (response.valid) {
          setIsValidToken(true);
        } else {
          toast.error("This reset link has expired or is invalid.", {
            position: "top-right",
            autoClose: 4000,
          });
          setTimeout(() => navigate("/forgot-password"), 2000);
        }
      } catch (error) {
        console.error("Token verification error:", error);
        toast.error(error.message || "Invalid or expired reset link.", {
          position: "top-right",
          autoClose: 4000,
        });
        setTimeout(() => navigate("/forgot-password"), 2000);
      } finally {
        setIsVerifyingToken(false);
      }
    };

    verifyToken();
  }, [token, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!token) {
      toast.error("Invalid or missing reset token", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log("Resetting password with token:", token);
      const response = await resetPassword(token, formData.password, formData.confirmPassword);
      console.log("Password reset response:", response);
      
      toast.success(response.message || "Password reset successfully! You can now sign in with your new password.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setIsSuccess(true);
      navigate("/login");
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error(error.message || "Something went wrong. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while verifying token
  if (isVerifyingToken) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row">
        {/* Left Side - Illustration */}
        <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
          <img
            src={loginIllustration}
            alt="Illustration"
            className="max-w-[70%] h-auto"
          />
        </div>

        {/* Right Side - Loading */}
        <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-secondary" />
            <p className="text-gray-600 font-bold">Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if token is invalid
  if (!isValidToken) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row">
        {/* Left Side - Illustration */}
        <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
          <img
            src={loginIllustration}
            alt="Illustration"
            className="max-w-[70%] h-auto"
          />
        </div>

        {/* Right Side - Error */}
        <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <img src={logo} alt="Logo" className="w-32 md:w-50 md:h-50" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
                Invalid Reset Link
              </h1>
            </div>

            <div className="space-y-6 border p-6 rounded-xl">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-6">
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
                
                <Link
                  to="/forgot-password"
                  className="w-full inline-block bg-secondary hover:bg-teal-700 text-white font-bold py-3 px-4 rounded-xl transition-colors text-sm"
                >
                  Request New Reset Link
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-[#075038] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Password reset successful
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Your password has been successfully updated
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Password updated successfully
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Your password has been reset. You can now sign in with your new password.
              </p>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#075038] hover:bg-[#075038]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075038] transition-colors"
              >
                Sign in to your account
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-[#075038] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Illustration */}
      <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
        <img
          src={loginIllustration}
          alt="Illustration"
          className="max-w-[70%] h-auto"
        />
      </div>

      {/* Right Side - Reset Password Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md space-y-6">
          {/* Logo and Branding */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src={logo} alt="Logo" className="w-32 md:w-50 md:h-50" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
              Reset your password
            </h1>
          </div>

          <div className="space-y-6 border p-6 rounded-xl">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  New password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-sm`}
                    placeholder="Enter your new password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Input */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Confirm new password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border ${
                      errors.confirmPassword ? "border-red-500" : "border-gray-300"
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-sm`}
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-secondary hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 text-sm"
              >
                {isLoading ? "Updating password..." : "Update password"}
              </button>
            </form>

            {/* Back to Sign In */}
            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-gray-600 hover:text-secondary transition-colors font-bold"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
